import React, { useState, useEffect } from "react";
import {
  TouchableOpacity,
  View,
  Pressable,
  ScrollView,
  Platform,
} from "react-native";
import styled, { useTheme } from "styled-components/native";
import { shareMenuTypes } from "../ShareMenu/types";
import { shareMenuOptions } from "../GalleryView/utils/types";
import { ShareVia, useNativeShare } from "../Share/utils/utils";
import { useTranslation } from "react-i18next";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { useDispatch } from "react-redux";
import { setLoginModalType } from "@redux/slices/appStateSlice";
import {
  filterDataByPriceMode,
  getBoxShadow,
} from "@utils/product/productValidators";
import { useShareOptionConfig } from "@redux/slices/globalSlice";
import { useOrgSettingsSlice } from "@redux/slices/orgSettingsSlice";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { VDBIcons } from "@utils/VDBIcons";
import { VDBText } from "@components/atoms";
import { SpaceType } from "@components/atoms/Spacer/types";
import LineViewContainer from "@scenes/requests/utils/LineView";
import { SearchResultsAndFavoritesEnum } from "@utils/product/searchResultConstants";
import { useScreenWiseShareOptionConfig } from "@redux/slices/unifiedProductPages";
import Routes from "@navigations/Routes";

const ShareMenuiPadWrapper = ({
  setSelectedOption,
  setshowShareModal,
  addExtraPadding,
  itemDetail,
  mediaUrl,
  setShareMenuOpen,
  isOpen,
  onShareViaNative,
  productType,
}: shareMenuTypes) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const theme = useTheme();
  const userInfo = useUserInfo();
  const { t } = useTranslation();
  const { isVDBOrg } = useOrgSettingsSlice();
  const typography = useTypographyStore();
  const isGuest = userInfo.isGuest;
  const dispatch = useDispatch();
  const { handleShareViaNative } = useNativeShare(itemDetail);

  const getScreenWiseShareOptionConfig = useScreenWiseShareOptionConfig(
    productType,
    Routes.PDPScreen
  );
  const screenWiseShareOptionConfig = filterDataByPriceMode(
    Object.values(getScreenWiseShareOptionConfig || {}),
    userInfo.getPriceMode()
  );
  const getShareOptionConfig = filterDataByPriceMode(
    Object.values(useShareOptionConfig()),
    userInfo.getPriceMode()
  );
  const shareOptions =
    screenWiseShareOptionConfig?.length > 0
      ? screenWiseShareOptionConfig
      : getShareOptionConfig?.length > 0
      ? getShareOptionConfig
      : shareMenuOptions(t, isVDBOrg);

  useEffect(() => {
    if (isOpen !== undefined) {
      setIsDropdownOpen(isOpen);
    }
  }, [isOpen]);

  const handleTriggerPress = () => {
    if (isGuest) {
      dispatch(setLoginModalType("default"));
      return;
    }

    const newOpenState = !isDropdownOpen;
    setIsDropdownOpen(newOpenState);
    if (setShareMenuOpen) {
      setShareMenuOpen(newOpenState);
    }
  };

  const menuOptionSelected = (eventName: SearchResultsAndFavoritesEnum) => {
    setIsDropdownOpen(false);
    if (setShareMenuOpen) {
      setShareMenuOpen(false);
    }

    switch (eventName) {
      case SearchResultsAndFavoritesEnum.SHARE_EMAIL:
        setSelectedOption(ShareVia.EMAIL);
        setshowShareModal(true);
        break;
      case SearchResultsAndFavoritesEnum.SHARE_NATIVE:
        setSelectedOption(ShareVia.NATIVE);
        setshowShareModal(true);
        break;
      case SearchResultsAndFavoritesEnum.SHARE_MEDIA:
        if (onShareViaNative && mediaUrl && Platform.OS !== "web") {
          onShareViaNative(mediaUrl);
        } else {
          handleShareViaNative(mediaUrl);
        }
        break;
      case SearchResultsAndFavoritesEnum.SHARE_VIA_LINK:
        setSelectedOption(ShareVia.LINK);
        setshowShareModal(true);
        break;
    }
  };

  const renderMenuOptions = () => {
    if (!shareOptions) return null;

    return shareOptions
      .filter((option) => mediaUrl || option?.name !== "share_media")
      .map((option, index) => (
        <TouchableOpacity
          key={option?.name}
          onPress={() => menuOptionSelected(option?.name)}
          activeOpacity={0.7}
        >
          <MenuItemContainer>
            <MenuItemStyle optionSize={shareOptions.length} index={index}>
              <VDBText color={theme["core-05"]} type={typography["Minor-M"]}>
                {Platform.OS === "web" && option.webLabel
                  ? t(option.webLabel, t(option.label))
                  : t(option.label)}
              </VDBText>
            </MenuItemStyle>
            {index < shareOptions.length - 1 && (
              <LineViewContainer background={"core-03"} />
            )}
          </MenuItemContainer>
        </TouchableOpacity>
      ));
  };

  return (
    <View style={{ position: "relative" }}>
      <TouchableOpacity
        onPress={handleTriggerPress}
        style={{ marginLeft: addExtraPadding ? 12 : 0 }}
        activeOpacity={0.8}
      >
        <IconContainer>
          <VDBIcon
            name={VDBIcons.SHARE}
            size={24}
            color={isDropdownOpen ? theme["core-05"] : theme["core-06"]}
            variant={IconsVariant.VDBCustom}
          />
        </IconContainer>
      </TouchableOpacity>

      {isDropdownOpen && (
        <>
          <Pressable
            style={{
              position: "absolute",
              top: -1000,
              left: -1000,
              right: -1000,
              bottom: -1000,
              zIndex: 998,
            }}
            onPress={() => {
              setIsDropdownOpen(false);
              if (setShareMenuOpen) {
                setShareMenuOpen(false);
              }
            }}
          />

          <DropdownContainer
            theme={theme}
            style={{
              position: "absolute",
              top: 48,
              right: addExtraPadding ? -12 : 0,
              width: 250,
              maxHeight: 300,
              backgroundColor: theme["core-01"],
              borderRadius: 8,
              zIndex: 999,
            }}
          >
            <ScrollView showsVerticalScrollIndicator={false} bounces={false}>
              {renderMenuOptions()}
            </ScrollView>
          </DropdownContainer>
        </>
      )}
    </View>
  );
};

const IconContainer = styled.View`
  padding: ${SpaceType.Basic}px;
`;

const DropdownContainer = styled.View`
  overflow: hidden;
  box-shadow: ${({ theme }) =>
    theme.Shadows?.Huge
      ? getBoxShadow(theme.Shadows.Huge)
      : "0px 2px 8px rgba(0, 0, 0, 0.25)"};
`;

const MenuItemContainer = styled.View`
  background-color: ${({ theme }) => theme["core-01"]};
`;

const MenuItemStyle = styled.View<{
  optionSize: number;
  index: number;
}>`
  border-color: #000000;
  width: 250px;
  padding: 10px;
  border-top-left-radius: ${({ optionSize, index }) =>
    optionSize === 1 || index === 0 ? 8 : 0}px;
  border-top-right-radius: ${({ optionSize, index }) =>
    optionSize === 1 || index === 0 ? 8 : 0}px;
  border-bottom-left-radius: ${({ optionSize, index }) =>
    index === optionSize - 1 ? 8 : 0}px;
  border-bottom-right-radius: ${({ optionSize, index }) =>
    index === optionSize - 1 ? 8 : 0}px;
`;

export default ShareMenuiPadWrapper;
